/* global globalThis */
import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha, Box, Button, Chip, CircularProgress, Drawer, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CheckRounded as CheckRoundedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import {
  C,
  FocusCard,
  candidateRowPropType,
  fmtPct,
  fmtInteger,
  localizedText,
  toneColor,
  resolveAccumulationStage,
  getFactorVisuals,
  getCriteriaMeta,
  getAccumulationSignalMeta,
  getResistanceDistanceTone,
  getDrawdownTone,
  hasValue,
  absorptionMetricLabel,
  getTierMeta,
  sectorDisplay,
  formatStateLabel,
  formatCheckLabel,
  describeMarketRegime,
  toneForFlag,
} from './shared';

const waitForRenderFrames = (count = 2) => new Promise((resolve) => {
  const tick = (remaining) => {
    if (remaining <= 0) {
      resolve();
      return;
    }
    globalThis.requestAnimationFrame(() => tick(remaining - 1));
  };
  tick(count);
});

const exportDrawerCapture = async ({ captureNode, scrollNode, fileName, backgroundColor = '#ffffff' }) => {
  if (!captureNode || !scrollNode) return;
  const paperNode = captureNode.closest('.MuiDrawer-paper');
  const toolbarNode = captureNode.querySelector('[data-export-header-toolbar="true"]');
  const restoreFns = [];

  try {
    if (toolbarNode instanceof HTMLElement) {
      const hadIgnore = Object.hasOwn(toolbarNode.dataset, 'html2canvasIgnore');
      const prevVisibility = toolbarNode.style.visibility;
      const prevPointerEvents = toolbarNode.style.pointerEvents;
      restoreFns.push(() => {
        if (hadIgnore) toolbarNode.dataset.html2canvasIgnore = 'true';
        else delete toolbarNode.dataset.html2canvasIgnore;
        toolbarNode.style.visibility = prevVisibility;
        toolbarNode.style.pointerEvents = prevPointerEvents;
      });
      delete toolbarNode.dataset.html2canvasIgnore;
      toolbarNode.style.visibility = 'hidden';
      toolbarNode.style.pointerEvents = 'none';
    }

    if (paperNode instanceof HTMLElement) {
      const prevOverflow = paperNode.style.overflow;
      const prevMaxHeight = paperNode.style.maxHeight;
      const prevHeight = paperNode.style.height;
      restoreFns.push(() => {
        paperNode.style.overflow = prevOverflow;
        paperNode.style.maxHeight = prevMaxHeight;
        paperNode.style.height = prevHeight;
      });
      paperNode.style.overflow = 'visible';
      paperNode.style.maxHeight = 'none';
      paperNode.style.height = 'auto';
    }

    {
      const prevHeight = captureNode.style.height;
      const prevMinHeight = captureNode.style.minHeight;
      const prevOverflow = captureNode.style.overflow;
      restoreFns.push(() => {
        captureNode.style.height = prevHeight;
        captureNode.style.minHeight = prevMinHeight;
        captureNode.style.overflow = prevOverflow;
      });
      captureNode.style.height = 'auto';
      captureNode.style.minHeight = '0';
      captureNode.style.overflow = 'visible';
    }

    {
      const prevOverflowY = scrollNode.style.overflowY;
      const prevHeight = scrollNode.style.height;
      const prevMaxHeight = scrollNode.style.maxHeight;
      const prevFlex = scrollNode.style.flex;
      const prevScrollTop = scrollNode.scrollTop;
      restoreFns.push(() => {
        scrollNode.style.overflowY = prevOverflowY;
        scrollNode.style.height = prevHeight;
        scrollNode.style.maxHeight = prevMaxHeight;
        scrollNode.style.flex = prevFlex;
        scrollNode.scrollTop = prevScrollTop;
      });
      scrollNode.style.overflowY = 'visible';
      scrollNode.style.height = 'auto';
      scrollNode.style.maxHeight = 'none';
      scrollNode.style.flex = '0 0 auto';
    }

    captureNode.querySelectorAll('*').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const computedStyle = globalThis.getComputedStyle(element);
      if (computedStyle.minHeight && computedStyle.minHeight !== '0px' && computedStyle.minHeight !== 'auto') {
        const previousMinHeight = element.style.minHeight;
        restoreFns.push(() => {
          element.style.minHeight = previousMinHeight;
        });
        element.style.minHeight = '0';
      }
    });

    await waitForRenderFrames(2);
    globalThis.dispatchEvent(new Event('resize'));
    await waitForRenderFrames(3);

    const rect = captureNode.getBoundingClientRect();
    const exportWidth = Math.ceil(rect.width);
    const exportHeight = Math.ceil(Math.max(captureNode.scrollHeight, rect.height));

    const drawerCanvas = await html2canvas(captureNode, {
      backgroundColor,
      scale: 2,
      useCORS: true,
      width: exportWidth,
      height: exportHeight,
      windowWidth: globalThis.innerWidth,
      windowHeight: Math.max(exportHeight, globalThis.innerHeight),
      scrollX: 0,
      scrollY: 0,
    });

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = drawerCanvas.width;
    finalCanvas.height = drawerCanvas.height;
    const context = finalCanvas.getContext('2d');
    if (context) {
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      context.drawImage(drawerCanvas, 0, 0);
    }

    const link = document.createElement('a');
    link.download = fileName;
    link.href = finalCanvas.toDataURL('image/png');
    link.click();
  } finally {
    while (restoreFns.length) {
      const restore = restoreFns.pop();
      try {
        restore?.();
      } catch (error) {
        console.debug('Failed to restore drawer export styles', error);
      }
    }
  }
};

export function DetailDrawer({ row, open, onClose, t, locale, lang }) {
  const [exportingImage, setExportingImage] = useState(false);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);

  const handleExportDrawerImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || exportingImage) return;

    try {
      setExportingImage(true);
      const asOfDate = row?.score_date || new Date().toISOString().slice(0, 10);
      await exportDrawerCapture({
        captureNode: drawerCaptureRef.current,
        scrollNode: drawerScrollRef.current,
        fileName: `${row?.ticker || 'early-accumulation'}-drawer-${asOfDate}.png`,
      });
    } catch (error) {
      console.error('Failed to export early accumulation drawer image', error);
    } finally {
      setExportingImage(false);
    }
  }, [exportingImage, row]);

  if (!row) return null;

  const tone = toneForFlag(row);
  const stageKey = resolveAccumulationStage(row, 'WATCH');
  const state = formatStateLabel(t, row);
  const tierMetaInfo = row.tier ? getTierMeta(row.tier, t) : null;
  const companyName = row?.company_name || row?.ticker || '-';
  const marketRegimeLabel = describeMarketRegime(row?.market_regime, lang);
  const score = Number(row?.early_accumulation_score) || 0;
  const criteria = row?.diagnostics?.criteria_passed ?? 0;
  const accumRatio = row.metrics?.accumulation_ratio;
  const absorptionMetric = row.display?.metrics?.absorption;
  const absorptionInsight = row.display?.insights?.absorption;
  const absorptionTone = toneColor(absorptionInsight?.tone || absorptionMetric?.tone);
  const absorptionText = localizedText(absorptionMetric?.text, lang);
  const absorptionSummary = localizedText(absorptionInsight?.summary, lang);
  const absorptionEvidence = (absorptionInsight?.evidence || []).map((item) => ({
    ...item,
    label: localizedText(item?.text, lang),
  }));

  const factors = [
    { key: 'marketSideways', value: row.components?.market_sideways_score, weight: null },
    { key: 'relativeStrength', value: row.components?.relative_strength_score, weight: 25 },
    { key: 'baseCompression', value: row.components?.base_compression_score, weight: 25 },
    { key: 'moneyFlow', value: row.components?.money_flow_score, weight: 20 },
    { key: 'trendStructure', value: row.components?.trend_structure_score, weight: 15 },
    { key: 'riskControl', value: row.components?.risk_control_score, weight: 15 },
  ];

  const metrics = [
    { key: 'currentPrice', value: fmtInteger(row.price?.close, locale) },
    { key: 'support20', value: fmtInteger(row.price?.support_20d, locale) },
    { key: 'distFromHigh', value: fmtPct(row.price?.dist_from_resistance, locale, 1) },
    { key: 'baseRange', value: fmtPct(row.metrics?.range20, locale, 100, 1) },
    { key: 'avgLiquidity', value: hasValue(row.liquidity?.avg_value_20d) ? row.liquidity.avg_value_20d.toLocaleString(locale) : '-' },
    { key: 'maxDrawdown', value: fmtPct(row.metrics?.drawdown20, locale) },
  ];

  const verdict = (() => {
    if (score >= 75) return { tone: C.emerald, key: 'excellent' };
    if (score >= 65) return { tone: C.blue, key: 'good' };
    if (score >= 55) return { tone: '#0ea5e9', key: 'starting' };
    return { tone: C.amber, key: 'insufficient' };
  })();

  const sectionTitle = (text) => (
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', letterSpacing: '0.02em', mb: 0.9 }}>{text}</Typography>
  );

  const drawerSectionSx = {
    mb: 1.6,
    p: 1.2,
    borderRadius: 1.35,
    bgcolor: '#fbfcfe',
    boxShadow: 'inset 0 0 0 1px rgba(226, 232, 240, 0.82)',
  };

  const passed = row.diagnostics?.passed_checks || [];
  const failed = row.diagnostics?.failed_checks || [];
  const factorRows = factors.map((factor) => {
    const value = Number(factor.value) || 0;
    const factorVisuals = getFactorVisuals(value, t);
    return {
      ...factor,
      valueNum: value,
      pct: Math.max(0, Math.min(100, value)),
      barColor: factorVisuals.barColor,
      verdictWord: factorVisuals.verdictWord,
      fLabel: t(`earlyAccV2.drawer.factors.${factor.key}.label`),
      fHint: t(`earlyAccV2.drawer.factors.${factor.key}.hint`),
    };
  });
  const strongestFactors = [...factorRows].sort((a, b) => b.valueNum - a.valueNum).slice(0, 2);
  const watchouts = failed.length > 0
    ? failed.slice(0, 2).map((item) => ({ key: item, label: formatCheckLabel(t, item), hint: null, tone: C.amber }))
    : [...factorRows]
      .sort((a, b) => a.valueNum - b.valueNum)
      .slice(0, 2)
      .map((item) => ({ key: item.key, label: item.fLabel, hint: item.fHint, tone: item.barColor }));

  const actionMeta = (() => {
    switch (stageKey) {
      case 'BREAKOUT':
        return { tone: C.emerald, title: t('earlyAccV2.drawer.actions.BREAKOUT.title'), body: t('earlyAccV2.drawer.actions.BREAKOUT.body') };
      case 'MATURE':
        return { tone: C.blue, title: t('earlyAccV2.drawer.actions.MATURE.title'), body: t('earlyAccV2.drawer.actions.MATURE.body') };
      case 'FORMING':
        return { tone: '#0ea5e9', title: t('earlyAccV2.drawer.actions.FORMING.title'), body: t('earlyAccV2.drawer.actions.FORMING.body') };
      case 'NEAR_MISS':
        return { tone: C.amber, title: t('earlyAccV2.drawer.actions.NEAR_MISS.title'), body: t('earlyAccV2.drawer.actions.NEAR_MISS.body') };
      default:
        return { tone: C.slate, title: state, body: t('earlyAccV2.drawer.actions.WATCH.body') };
    }
  })();

  const criteriaMeta = getCriteriaMeta(criteria);
  const accumMeta = getAccumulationSignalMeta(accumRatio);
  const primaryMetrics = [
    { key: 'currentPrice', value: fmtInteger(row.price?.close, locale), tone: C.ink },
    { key: 'support20', value: fmtInteger(row.price?.support_20d, locale), tone: C.blue },
    { key: 'distFromHigh', value: fmtPct(row.price?.dist_from_resistance, locale, 1), tone: getResistanceDistanceTone(row.price?.dist_from_resistance) },
    { key: 'maxDrawdown', value: fmtPct(row.metrics?.drawdown20, locale), tone: getDrawdownTone(row.metrics?.drawdown20) },
  ];
  const secondaryMetrics = metrics.filter((metric) => !['currentPrice', 'support20', 'distFromHigh', 'maxDrawdown'].includes(metric.key));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, p: 0 } } }}>
      <Box ref={drawerCaptureRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff' }}>
        <Box sx={{ px: 2.5, py: 1.6, bgcolor: alpha('#ffffff', 0.7), display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.25, boxShadow: 'inset 0 -1px 0 rgba(238, 242, 247, 1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 0.75, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.88rem', lineHeight: 1, letterSpacing: '0.02em' }}>{row?.ticker || '-'}</Typography>
              </Box>
              <Box sx={{ height: 12, bgcolor: tone, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '0.5rem', letterSpacing: '0.12em', lineHeight: 1 }}>ACC</Typography>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 850, fontSize: '1rem', color: '#0f172a', lineHeight: 1.15, letterSpacing: '-0.015em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={companyName}>
                  {companyName}
                </Typography>
              </Box>
              <Typography sx={{ mt: 0.4, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sectorDisplay(row, lang)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-html2canvas-ignore="true" data-export-header-toolbar="true">
            <Tooltip title={exportingImage ? t('common.exportingImage', 'Dang xuat anh...') : t('common.exportImage', 'Xuat anh')} arrow>
              <span>
                <IconButton onClick={handleExportDrawerImage} disabled={exportingImage} size="small" sx={{ border: '1px solid #e2e8f0', bgcolor: '#fff', color: '#475569', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)', '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' } }}>
                  {exportingImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small" sx={{ border: '1px solid #e2e8f0', bgcolor: '#fff', color: '#475569', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)', '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <Box ref={drawerScrollRef} data-export-scroll-root="true" sx={{ p: 1.75, overflowY: 'auto', flex: 1, bgcolor: '#ffffff' }}>
          <Box sx={{ mb: 1.6 }}>
            <Box sx={{ maxWidth: 560 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.8, rowGap: 0.3, mb: 0.7 }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: tone, lineHeight: 1.2 }}>{state}</Typography>
                {tierMetaInfo ? <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', lineHeight: 1.2 }}>• {row.tier} · {tierMetaInfo.label}</Typography> : null}
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', lineHeight: 1.2 }}>• {marketRegimeLabel}</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.8 }}>
                <Tooltip arrow placement="top" title={t('earlyAccV2.drawer.hero.scoreTip')}>
                  <Typography sx={{ fontSize: '1.56rem', fontWeight: 900, color: tone, lineHeight: 1.1, letterSpacing: '-0.03em' }}>{t(`earlyAccV2.drawer.hero.scoreValue.${verdict.key}`)}</Typography>
                </Tooltip>
                <Chip size="small" label={actionMeta.title} sx={{ height: 24, borderRadius: '999px', px: 0.35, fontSize: '0.72rem', fontWeight: 800, bgcolor: alpha(actionMeta.tone, 0.1), color: actionMeta.tone, border: `1px solid ${alpha(actionMeta.tone, 0.2)}` }} />
              </Box>
              <Typography sx={{ mt: 0.45, fontSize: '0.78rem', color: '#475569', lineHeight: 1.6 }}>
                <Box component="span" sx={{ color: criteriaMeta.tone, fontWeight: 800 }}>{t(`earlyAccV2.drawer.hero.criteriaText.${criteriaMeta.textKey}`)}</Box>,{' '}
                <Box component="span" sx={{ color: accumMeta.tone, fontWeight: 800 }}>{t(`earlyAccV2.drawer.hero.accumText.${accumMeta.textKey}`)}</Box>.
              </Typography>
            </Box>
          </Box>

          <Box sx={drawerSectionSx}>
            {sectionTitle(t('earlyAccV2.drawer.sections.focus'))}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
              <FocusCard eyebrow={t('earlyAccV2.drawer.focus.status')} title={t(`earlyAccV2.drawer.verdict.${verdict.key}.title`)} body={t(`earlyAccV2.drawer.verdict.${verdict.key}.body`)} tone={verdict.tone} />
              <FocusCard eyebrow={t('earlyAccV2.drawer.focus.action')} title={actionMeta.title} body={actionMeta.body} tone={actionMeta.tone} />
              <FocusCard eyebrow={t('earlyAccV2.drawer.focus.strengths')} tone={C.emerald}>
                <Stack spacing={0.7} sx={{ mt: 0.15 }}>
                  {strongestFactors.map((item) => (
                    <Box key={`strong-${item.key}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.7 }}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: C.emerald, mt: '2px' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.35 }}>{item.fLabel}</Typography>
                        <Typography sx={{ fontSize: '0.64rem', color: '#64748b', lineHeight: 1.4 }}>{item.fHint}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </FocusCard>
              <FocusCard eyebrow={t('earlyAccV2.drawer.focus.watchouts')} tone={C.amber}>
                <Stack spacing={0.7} sx={{ mt: 0.15 }}>
                  {watchouts.map((item) => (
                    <Box key={`watch-${item.key || item.label}-${item.hint || ''}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.7 }}>
                      <CancelIcon sx={{ fontSize: 14, color: item.tone || C.amber, mt: '2px' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.35 }}>{item.label}</Typography>
                        {item.hint ? <Typography sx={{ fontSize: '0.64rem', color: '#64748b', lineHeight: 1.4 }}>{item.hint}</Typography> : null}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </FocusCard>
            </Box>
          </Box>

          <Box sx={drawerSectionSx}>
            {sectionTitle(t('earlyAccV2.drawer.sections.levels'))}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
              {primaryMetrics.map((metric) => (
                <Box key={`primary-${metric.key}`} sx={{ p: 1.05, borderRadius: 1.05, bgcolor: alpha(metric.tone, 0.04), boxShadow: `inset 0 0 0 1px ${alpha(metric.tone, 0.1)}` }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t(`earlyAccV2.drawer.metrics.${metric.key}.label`)}</Typography>
                  <Typography sx={{ mt: 0.2, fontSize: '1rem', fontWeight: 800, color: metric.tone, lineHeight: 1.15 }}>{metric.value}</Typography>
                  <Typography sx={{ mt: 0.35, fontSize: '0.64rem', color: '#64748b', lineHeight: 1.4 }}>{t(`earlyAccV2.drawer.metrics.${metric.key}.hint`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {(absorptionText || absorptionSummary) && (
            <Box sx={drawerSectionSx}>
              {sectionTitle(lang === 'vi' ? 'Ho tro danh gia' : 'Investor support')}
              <Box sx={{ p: 1.15, borderRadius: 1.1, bgcolor: alpha(absorptionTone, 0.05), boxShadow: `inset 0 0 0 1px ${alpha(absorptionTone, 0.12)}` }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: absorptionSummary ? 0.65 : 0 }}>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: alpha(absorptionTone, 0.9), textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lang === 'vi' ? 'Lop hap thu' : 'Absorption layer'}</Typography>
                  {absorptionText ? <Chip size="small" label={absorptionMetricLabel(absorptionText, absorptionMetric?.value)} sx={{ height: 23, borderRadius: '999px', fontSize: '0.68rem', fontWeight: 800, bgcolor: alpha(absorptionTone, 0.1), color: absorptionTone, border: `1px solid ${alpha(absorptionTone, 0.2)}` }} /> : null}
                </Box>
                {absorptionSummary ? <Typography sx={{ fontSize: '0.72rem', color: '#475569', lineHeight: 1.6 }}>{absorptionSummary}</Typography> : null}
                {absorptionEvidence.length > 0 && (
                  <Stack spacing={0.8} sx={{ mt: 1 }}>
                    {absorptionEvidence.map((item) => {
                      const itemTone = toneColor(item?.tone);
                      return (
                        <Box key={item.key || item.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.7 }}>
                          <CheckRoundedIcon sx={{ fontSize: 14, color: itemTone, mt: '2px' }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.4 }}>{item.label}</Typography>
                          </Box>
                          {hasValue(item?.value) ? <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: itemTone, flexShrink: 0 }}>{item.value}</Typography> : null}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Box>
          )}

          <Box sx={drawerSectionSx}>
            {sectionTitle(t('earlyAccV2.drawer.sections.factors'))}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
              {factorRows.map((item) => (
                <Box key={item.key} sx={{ p: 1.05, borderRadius: 1.05, bgcolor: '#ffffff', boxShadow: `inset 0 0 0 1px ${alpha(item.barColor, 0.12)}` }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 0.8, mb: 0.45 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.35 }}>{item.fLabel}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: item.barColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.verdictWord}</Typography>
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: item.barColor, minWidth: 28, textAlign: 'right' }}>{item.valueNum.toFixed(0)}</Typography>
                  </Box>
                  <Box sx={{ position: 'relative', height: 5, borderRadius: 999, bgcolor: '#eef2f7', overflow: 'hidden', mb: 0.45 }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${item.pct}%`, bgcolor: item.barColor, borderRadius: 999, transition: 'width 0.3s' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.64rem', color: '#64748b', lineHeight: 1.4 }}>{item.fHint}{item.weight ? ` (${item.weight}${t('earlyAccV2.drawer.weightSuffix')})` : ''}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={drawerSectionSx}>
            {sectionTitle(t('earlyAccV2.drawer.sections.metrics'))}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, rowGap: 1, columnGap: 1 }}>
              {secondaryMetrics.map((metric) => (
                <Box key={metric.key} sx={{ p: 0.95, borderRadius: 1, bgcolor: '#ffffff', boxShadow: 'inset 0 0 0 1px rgba(226, 232, 240, 0.72)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t(`earlyAccV2.drawer.metrics.${metric.key}.label`)}</Typography>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: C.ink, fontFamily: 'inherit' }}>{metric.value}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', lineHeight: 1.4 }}>{t(`earlyAccV2.drawer.metrics.${metric.key}.hint`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {(passed.length > 0 || failed.length > 0) && (
            <Box sx={{ ...drawerSectionSx, mb: 0.5 }}>
              {sectionTitle(t('earlyAccV2.drawer.sections.criteria', { passed: passed.length, failed: failed.length }))}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {passed.map((item) => (
                  <Box key={`p-${item}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.7, py: 0.25, borderRadius: 0.5, bgcolor: alpha(C.emerald, 0.08), border: `1px solid ${alpha(C.emerald, 0.2)}` }}>
                    <Typography sx={{ fontSize: '0.7rem', color: C.emerald, fontWeight: 800, lineHeight: 1, display: 'inline-flex' }}><CheckCircleIcon sx={{ fontSize: 13 }} /></Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#475569', fontWeight: 600 }}>{formatCheckLabel(t, item)}</Typography>
                  </Box>
                ))}
                {failed.map((item) => (
                  <Box key={`f-${item}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.7, py: 0.25, borderRadius: 0.5, bgcolor: alpha(C.amber, 0.06), border: `1px solid ${alpha(C.amber, 0.2)}` }}>
                    <Typography sx={{ fontSize: '0.7rem', color: C.amber, fontWeight: 800, lineHeight: 1, display: 'inline-flex' }}><CancelIcon sx={{ fontSize: 13 }} /></Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#475569', fontWeight: 600 }}>{formatCheckLabel(t, item)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ px: 1.75, py: 1.1, bgcolor: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: 1, boxShadow: 'inset 0 1px 0 rgba(226, 232, 240, 0.75)' }}>
          <Button component={RouterLink} to={`/stocks/${row.ticker}`} variant="contained" size="small" sx={{ textTransform: 'none', fontWeight: 800, fontSize: '0.74rem', boxShadow: 'none', borderRadius: 1, px: 1.4, '&:hover': { boxShadow: 'none' } }}>
            {t('earlyAccV2.drawer.openAnalysis', { ticker: row.ticker })} {'->'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

DetailDrawer.propTypes = {
  row: candidateRowPropType,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  t: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired,
};
